import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, ReplaySubject } from 'rxjs';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { environment } from 'src/environments/environment';
import { UserLoaderGuard } from '../userLoader/user-loader.guard';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsGuard {
  private readonly INTERNAL_ACCESS_KEY = 'zenity_internal_access';
  private readonly INTERNAL_ACCESS_PARAM = 'zenity_internal';

  constructor(
    private readonly _featureFlagService: FeatureFlagsService,
    private readonly _userLoaderGuard: UserLoaderGuard,
    private readonly _router: Router,
    private readonly _activeRoute: ActivatedRoute
  ) {}

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const hasAccess$: ReplaySubject<boolean> = new ReplaySubject(1);

    this._userLoaderGuard.canActivate(route, state).subscribe({
      next: (isActivated) => {
        if (isActivated) {
          // Get the route path, handling edge cases
          const routePath = route.url?.[0]?.path;

          if (!routePath) {
            // If no route path, allow access (return to app)
            hasAccess$.next(true);
            return;
          }

          // Check for internal access parameter (for issues page secret access)
          this._checkInternalAccess(route, routePath);

          // Check if internal access is enabled for issues route
          if (routePath === 'issues' && this._hasInternalAccess()) {
            hasAccess$.next(true);
            return;
          }

          const hasAccess = this._featureFlagService.canActivateRoute(routePath);

          if (!hasAccess) {
            window.location.href = `${environment.app_base_url}/app`;
            hasAccess$.next(false);
          } else {
            hasAccess$.next(true);
          }
        } else {
          window.location.href = `${environment.app_base_url}/app`;
          hasAccess$.next(false);
        }
      },
      error: (error) => {
        console.log(error);
        window.location.href = `${environment.app_base_url}/app`;
        hasAccess$.next(false);
      },
    });

    return hasAccess$;
  }

  private _checkInternalAccess(route: ActivatedRouteSnapshot, routePath: string): void {
    // Only for issues route
    if (routePath !== 'issues') {
      return;
    }

    // Check URL parameter
    const internalParam = route.queryParams[this.INTERNAL_ACCESS_PARAM];
    if (internalParam === 'true') {
      sessionStorage.setItem(this.INTERNAL_ACCESS_KEY, 'true');
    }
  }

  private _hasInternalAccess(): boolean {
    return sessionStorage.getItem(this.INTERNAL_ACCESS_KEY) === 'true';
  }
}
